// Layout.jsx — new shell component for ui-v2
// Renders sidebar + topbar + inner tab bar
// All modules render inside this as children
import { useState, useEffect } from 'react';
import { db, doc, getDoc, updateDoc, auth, signOut } from '../firebase/config';
import { useAppContext } from '../AppContext';

const NAV = [
  { id:'orders',     label:'Orders',        icon:'ti-clipboard-list', section:'Main' },
  { id:'attendance', label:'Attendance',     icon:'ti-users',          section:'Main' },
  { id:'production', label:'Production',     icon:'ti-building-factory-2', section:'Main' },
  { id:'tasks',      label:'Tasks & Notes',  icon:'ti-check',          section:'Main' },
  { id:'salary',     label:'Salary',         icon:'ti-wallet',         section:'Finance' },
  { id:'crates',     label:'Crate Tracker',  icon:'ti-box',            section:'Finance' },
  { id:'recipes',    label:'Recipes',        icon:'ti-book',           section:'Operations' },
  { id:'assets',     label:'Assets & Ops',   icon:'ti-tool',           section:'Operations' },
  { id:'admin',      label:'Admin',          icon:'ti-settings',       section:'Operations' },
];

const SECTIONS = ['Main','Finance','Operations'];

const MODULE_TABS = {
  orders:     [{id:'orders',label:'Upcoming'},{id:'calendar',label:'Calendar'},{id:'delivered',label:'Delivered'}],
  attendance: [{id:'mark',label:'Mark Attendance'},{id:'overview',label:'Staff Overview'}],
  salary:     [{id:'payroll',label:'Payroll'},{id:'advances',label:'Advances'},{id:'ctc',label:'CTC'},{id:'settings',label:'Config'}],
  recipes:    [{id:'recipes',label:'Recipes'},{id:'ingredients',label:'Ingredients'},{id:'subrecipes',label:'Sub Recipes'},{id:'creators',label:'Creators'}],
  admin:      [{id:'team',label:'Team'},{id:'modules',label:'Module Access'},{id:'customers',label:'Customers'},{id:'payhistory',label:'Payslip History'},{id:'pricequotes',label:'Price Quotes'},{id:'checklist',label:'Checklist'}],
  production: [{id:'hotsnacks',label:'Hot Snacks'},{id:'rawmat',label:'Raw Materials'},{id:'daily',label:'Daily Plan'},{id:'settings',label:'Settings'}],
  assets:     [{id:'assets',label:'Assets'},{id:'logs',label:'Maint. Log'},{id:'reminders',label:'Reminders'},{id:'compliance',label:'Compliance'},{id:'contacts',label:'Contacts'}],
  crates:     [{id:'entry',label:"Today's Entry"},{id:'log',label:'Log'},{id:'admin',label:'Settings'}],
  tasks:      [{id:'tasks',label:'Tasks'},{id:'notes',label:'Notes'}],
};

export default function Layout({
  module, onModule, tab, onTabChange,
  topbarRight, children
}){
  const { user, profile } = useAppContext();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [fontSize, setFontSize] = useState(() => localStorage.getItem('tof-fs') || 'medium');
  const [editLayoutOpen, setEditLayoutOpen] = useState(false);
  const [signOutConfirm, setSignOutConfirm] = useState(false);

  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);

  useEffect(() => {
    const sizes = { small: '12px', medium: '14px', large: '16px' };
    document.documentElement.style.setProperty('--fs', sizes[fontSize] || '14px');
    localStorage.setItem('tof-fs', fontSize);
  }, [fontSize]);

  const canAccess = (modId) => {
    if (!modId) return true;
    if (profile?.role === 'admin') return true;
    if (['admin','salary'].includes(modId)) return false;
    if (modId === 'assets') return profile?.modules?.includes('assets') || profile?.modules?.includes('assets_log');
    if (modId === 'crates') return profile?.modules?.includes('crates') || profile?.modules?.includes('crates_settings');
    if (modId === 'production') return profile?.modules?.includes('production') || profile?.modules?.includes('production_settings');
    const allowed = profile?.modules;
    if (!allowed) return true;
    return allowed.includes(modId);
  };

  const visibleNav = NAV.filter(n => canAccess(n.id));
  const tabs = MODULE_TABS[module] || null;
  const currentModule = module || 'dashboard';

  const moduleTitles = {
    dashboard: 'Dashboard', orders: 'Orders', attendance: 'Attendance',
    production: 'Production', tasks: 'Tasks & Notes', salary: 'Salary',
    crates: 'Crate Tracker', recipes: 'Recipes', assets: 'Assets & Ops', admin: 'Admin',
  };

  const greeting = (() => {
    const h = new Date().getHours();
    return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
  })();

  const today = new Date().toLocaleDateString('en-IN', { weekday:'long', day:'numeric', month:'short', year:'numeric' });

  const sidebarContent = (
    <div className="lyt-sidebar">
      <div className="lyt-sb-logo">
        <div className="lyt-sb-mark">T</div>
        <div>
          <div className="lyt-sb-name">True Origin Foods</div>
          <div className="lyt-sb-sub">Operations</div>
        </div>
      </div>
      {/* Home / Dashboard */}
      <div
        className={`lyt-nav${!currentModule ? ' on' : ''}`}
        onClick={() => { onModule(null); setMobileNavOpen(false); }}>
        <i className="ti ti-layout-dashboard" aria-hidden="true" />
        <span>Home</span>
      </div>

      {SECTIONS.map(sec => {
        const items = visibleNav.filter(n => n.section === sec);
        if (!items.length) return null;
        return (
          <div key={sec}>
            <div className="lyt-sb-sec">{sec}</div>
            {items.map(n => (
              <div key={n.id}
                className={`lyt-nav${currentModule === n.id ? ' on' : ''}`}
                onClick={() => { onModule(n.id); setMobileNavOpen(false); }}>
                <i className={`ti ${n.icon}`} aria-hidden="true" />
                <span>{n.label}</span>

              </div>
            ))}
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="lyt-shell" style={{ '--fs': fontSize === 'small' ? '12px' : fontSize === 'large' ? '16px' : '14px' }}>

      {/* Sidebar — desktop always visible, mobile as drawer */}
      {!isMobile && sidebarContent}
      {isMobile && mobileNavOpen && (
        <div className="lyt-mob-overlay" onClick={() => setMobileNavOpen(false)}>
          <div className="lyt-mob-drawer" onClick={e => e.stopPropagation()}>
            {sidebarContent}
          </div>
        </div>
      )}

      <div className="lyt-main">
        {/* Topbar */}
        <div className="lyt-topbar">
          <div className="lyt-tb-left">
            {isMobile && (
              <button className="lyt-hamburger" onClick={() => setMobileNavOpen(true)}>
                <i className="ti ti-menu-2" aria-hidden="true" />
              </button>
            )}
            <div>
              <div className="lyt-tb-title">{moduleTitles[currentModule] || 'Dashboard'}</div>
              <div className="lyt-tb-sub" style={{display: isMobile ? 'none' : 'block'}}>
                {currentModule === 'dashboard' ? `${today} · ${greeting}` : today}
              </div>
            </div>
          </div>
          <div className="lyt-tb-right">
            {topbarRight}
            <button className="lyt-tbb" onClick={() => setEditLayoutOpen(v => !v)}>
              <i className="ti ti-layout-grid" aria-hidden="true" />
              {!isMobile && ' Edit Layout'}
            </button>
            <button className="lyt-tbb lyt-tbb-icon">
              <i className="ti ti-bell" aria-hidden="true" />
            </button>
            {signOutConfirm ? (
              <div style={{ display:'flex', alignItems:'center', gap:5 }}>
                <span style={{ fontSize:'calc(var(--fs) - 2px)', color:'var(--lyt-t4)',
                  whiteSpace:'nowrap' }}>Sign out?</span>
                <button className="lyt-tbb" style={{ fontSize:'calc(var(--fs) - 2px)',
                  color:'var(--lyt-red)', fontWeight:700 }}
                  onClick={() => signOut(auth)}>Yes</button>
                <button className="lyt-tbb" style={{ fontSize:'calc(var(--fs) - 2px)' }}
                  onClick={() => setSignOutConfirm(false)}>No</button>
              </div>
            ) : (
              <button className="lyt-tbb" onClick={() => setSignOutConfirm(true)}>
                <i className="ti ti-user" aria-hidden="true" />
                {!isMobile && ` ${profile?.name || 'Admin'}`}
              </button>
            )}
          </div>
        </div>

        {/* Edit layout panel */}
        {editLayoutOpen && (
          <div className="lyt-edit-panel">
            <div className="lyt-ep-title">Edit Layout</div>
            <div className="lyt-ep-sec">Text Size</div>
            <div className="lyt-fs-row">
              {['small','medium','large'].map(s => (
                <button key={s} className={`lyt-fs-btn${fontSize===s?' on':''}`} onClick={() => setFontSize(s)}>
                  {s.charAt(0).toUpperCase()+s.slice(1)}
                </button>
              ))}
            </div>
            <div style={{marginTop:14,paddingTop:12,borderTop:'0.5px solid #E2E8F0'}}>
              <button className="lyt-tbb lyt-tbb-dark" style={{width:'100%',justifyContent:'center'}} onClick={() => setEditLayoutOpen(false)}>
                <i className="ti ti-check" aria-hidden="true" /> Done
              </button>
            </div>
          </div>
        )}

        {/* Inner tab bar — only for modules with sub-tabs */}
        {tabs && (
          <div className="lyt-tabbar">
            {tabs.map(t => (
              <button key={t.id}
                className={`lyt-utab${tab === t.id ? ' on' : ''}`}
                onClick={() => onTabChange(t.id)}>
                {t.label}
              </button>
            ))}
          </div>
        )}

        {/* Page content */}
        <div className="lyt-page">
          {children}
        </div>

        {/* Mobile bottom nav */}
        {isMobile && (
          <div className="lyt-mob-nav">
            {[
              { id:'dashboard', icon:'ti-layout-dashboard', label:'Home' },
              { id:'orders',    icon:'ti-clipboard-list',   label:'Orders' },
              { id:'attendance',icon:'ti-users',            label:'Attend.' },
              { id:'salary',    icon:'ti-wallet',           label:'Salary' },
            ].filter(n => n.id === 'dashboard' || canAccess(n.id)).map(n => (
              <div key={n.id}
                className={`lyt-mn${currentModule === n.id ? ' act' : ''}`}
                onClick={() => onModule(n.id === 'dashboard' ? null : n.id)}>
                <i className={`ti ${n.icon}`} aria-hidden="true" />
                <span>{n.label}</span>
              </div>
            ))}
            <div className="lyt-mn" onClick={() => setMobileNavOpen(true)}>
              <i className="ti ti-dots" aria-hidden="true" />
              <span>More</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
